import { useState } from 'react';
import { CircleCheck, CircleX, ExternalLink, KeyRound, Trash } from 'lucide-react';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Field } from '@/components/ui/Field';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import type { ApiKeyStatus, TestResult } from '@/types/integrations';
import type { ProviderSpec } from './integrations-catalog';
import { useRemoveProviderKey, useSetProviderKey, useTestProvider } from './use-integrations';

/**
 * Tarjeta de un proveedor de IA (DeepSeek / Gemini).
 *
 * Contrato del backend que se respeta tal cual:
 *  - la API key se guarda cifrada (AES-256-GCM) y NUNCA se devuelve: el estado que se pinta es
 *    `{ configurada, preview }` con una máscara (`••••1234`);
 *  - "Probar conexión" (`POST .../test`) responde SIEMPRE 200 y el veredicto viaja en
 *    `{ ok, message }` → se pinta como aviso, no como error de la petición. Si el body lleva
 *    `apiKey`, se prueba esa sin guardarla (sirve para validarla antes de guardar).
 */

export interface ProviderKeyCardProps {
  spec: ProviderSpec;
  status: ApiKeyStatus;
  /** ADMIN del negocio activo: sin ese rol solo se muestra el estado. */
  canEdit: boolean;
}

export function ProviderKeyCard({ spec, status, canEdit }: ProviderKeyCardProps) {
  const { tenantId } = useActiveTenantAccess();
  const setProviderKey = useSetProviderKey(tenantId);
  const removeProviderKey = useRemoveProviderKey(tenantId);
  const testProvider = useTestProvider(tenantId);

  const [apiKey, setApiKey] = useState('');
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const Icon = spec.icon;
  const saving = setProviderKey.isPending;
  const testing = testProvider.isPending;
  const canTest = status.configurada || apiKey.trim().length > 0;

  async function handleSave() {
    if (!apiKey.trim()) return;

    setError(null);
    setTestResult(null);
    try {
      await setProviderKey.mutateAsync({ provider: spec.provider, apiKey: apiKey.trim() });
      setApiKey('');
    } catch (caught) {
      setError(getErrorMessage(caught));
    }
  }

  async function handleTest() {
    setError(null);
    setTestResult(null);
    try {
      const key = apiKey.trim();
      setTestResult(
        await testProvider.mutateAsync({
          provider: spec.provider,
          ...(key.length > 0 ? { apiKey: key } : {}),
        }),
      );
    } catch (caught) {
      setError(getErrorMessage(caught));
    }
  }

  async function handleRemove() {
    setError(null);
    setTestResult(null);
    try {
      await removeProviderKey.mutateAsync(spec.provider);
      setConfirmRemove(false);
    } catch (caught) {
      setError(getErrorMessage(caught));
    }
  }

  return (
    <>
      <Card>
        <CardHeader
          actions={
            <Badge
              tone={status.configurada ? 'success' : 'warning'}
              dot
              icon={status.configurada ? <CircleCheck size={12} /> : <CircleX size={12} />}
            >
              {status.configurada ? 'Configurada' : 'Sin configurar'}
            </Badge>
          }
        >
          <CardTitle className="flex items-center gap-2">
            <span className="flex size-8 items-center justify-center rounded-control bg-brand-50 text-brand-600">
              <Icon size={16} />
            </span>
            {spec.label}
          </CardTitle>

          <CardDescription>{spec.description}</CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {error ? <Alert>{error}</Alert> : null}

          {testResult ? (
            <Alert tone={testResult.ok ? 'success' : 'warning'}>{testResult.message}</Alert>
          ) : null}

          {status.configurada ? (
            <p className="text-caption text-ink-muted">
              Key guardada: <span className="font-mono text-ink-soft">{status.preview ?? '••••'}</span>{' '}
              <span className="text-ink-faint">
                (por seguridad el backend nunca la devuelve completa)
              </span>
            </p>
          ) : null}

          {canEdit ? (
            <>
              <Field
                label={status.configurada ? 'Reemplazar la key' : 'API key'}
                htmlFor={`key-${spec.provider}`}
                hint={`Se guarda cifrada y asociada solo a este negocio. Se genera en ${spec.docsUrl}`}
              >
                <PasswordInput
                  id={`key-${spec.provider}`}
                  name={spec.provider}
                  autoComplete="off"
                  placeholder={spec.placeholder}
                  leftIcon={<KeyRound size={16} />}
                  value={apiKey}
                  onChange={(event) => setApiKey(event.target.value)}
                />
              </Field>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  leftIcon={<KeyRound size={16} />}
                  loading={saving}
                  disabled={apiKey.trim().length === 0}
                  onClick={() => void handleSave()}
                >
                  Guardar key
                </Button>

                <Button
                  variant="secondary"
                  loading={testing}
                  disabled={!canTest}
                  onClick={() => void handleTest()}
                >
                  Probar conexión
                </Button>

                {status.configurada ? (
                  <Button
                    variant="ghost"
                    className="text-danger-strong hover:bg-danger-soft"
                    leftIcon={<Trash size={16} />}
                    disabled={saving || testing}
                    onClick={() => setConfirmRemove(true)}
                  >
                    Quitar key
                  </Button>
                ) : null}

                <a
                  href={spec.docsUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="ml-auto inline-flex items-center gap-1.5 text-caption font-medium text-brand-700 hover:underline"
                >
                  Conseguir una key
                  <ExternalLink size={14} />
                </a>
              </div>
            </>
          ) : (
            <Alert tone="info">
              Solo un administrador del negocio puede guardar o probar esta key.
            </Alert>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        onConfirm={() => void handleRemove()}
        title={`Quitar la key de ${spec.label}`}
        description={`El bot dejará de usar ${spec.label}. La key no se puede recuperar desde aquí: para volver a activarlo tendrás que pegar una nueva.`}
        confirmLabel="Quitar key"
        loading={removeProviderKey.isPending}
        error={error}
      />
    </>
  );
}
